import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Eye, EyeOff, Package } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BG_IMAGE = 'https://images.pexels.com/photos/4484042/pexels-photo-4484042.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dbStatus, setDbStatus] = useState(null); // null = verificando, true = ok, false = erro
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const checkDb = async () => {
      try {
        await axios.get(`${API_URL}/api/ready`, { timeout: 5000 });
        setDbStatus(true);
      } catch {
        setDbStatus(false);
      }
    };
    checkDb();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      toast.success('Login realizado com sucesso!');
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="flex min-h-screen">
      {/* Form Side */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="mb-10">
            <div className="flex items-center gap-3" data-testid="login-logo">
              <div className="w-12 h-12 bg-[#1a365d] rounded-xl flex items-center justify-center">
                <Package className="w-7 h-7 text-white" />
              </div>
              <span className="text-2xl font-heading font-bold text-[#1a365d]">Conferência</span>
            </div>
            <p className="mt-4 text-slate-500">
              Faça login para acessar o sistema
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className="h-12 text-lg px-4 border-slate-300 focus:ring-2 focus:ring-[#1a365d] focus:border-transparent"
                required
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">
                Senha
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 text-lg px-4 pr-12 border-slate-300 focus:ring-2 focus:ring-[#1a365d] focus:border-transparent"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#1a365d] hover:bg-[#1a365d]/90 text-white text-lg font-medium shadow-md transition-all active:scale-95"
              data-testid="login-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Entrando...
                </>
              ) : (
                'Entrar'
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-10 text-center space-y-1">
            <p className="text-sm text-slate-400">
              Sistema de Conferência
            </p>
            <p className={`text-xs font-medium transition-colors duration-500 ${dbStatus === null
                ? 'text-slate-400'
                : dbStatus
                  ? 'text-green-500'
                  : 'text-red-500'
              }`}>
              {dbStatus === null ? '●  Verificando conexão...' : dbStatus ? '● Criado por Cod4x' : '● Criado por Cod4x'}
            </p>
          </div>
        </div>
      </div>

      {/* Image Side */}
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ backgroundImage: `url(${BG_IMAGE})` }}
      >
        <div className="absolute inset-0 bg-[#1a365d]/70" />
        <div className="relative z-10 flex flex-col items-center justify-center w-full p-12 text-white">
          <h2 className="text-4xl font-heading font-bold text-center mb-4">
            Sistema de Conferência de NF-e
          </h2>
          <p className="text-lg text-white/80 text-center max-w-md">
            Agilize a conferência de mercadorias com precisão e controle total sobre suas operações de expedição.
          </p>
        </div>
      </div>
    </div>
  );
}
